import {
  EMPTY,
  filter,
  map,
  mergeMap,
  type Observable,
  of,
  startWith,
  withLatestFrom,
} from 'rxjs'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import type { Message } from '@/features/messages/Message'
import { Id } from '@/infra/id'
import type { SystemNotification } from '@/features/activity'
import type { Conversation } from '@/features/messages/Conversation'
import { toStream } from '@/utils/mobx-util'

export interface IncomingMessage {
  message: Message
  conversation: Conversation
}

export interface IncomingJobMessageNotificationsOptions {
  incomingMessage$: Observable<IncomingMessage>
  activeJobConversationId$: Observable<Id | null>
  pageActivity$: Observable<PageActivityDescriptor>
  userActive$: Observable<boolean>
  onAction: (options: ActionOptions) => unknown
}

export interface ActionOptions {
  jobId: string
  /**
   * Whether the conversation was active at the time the message was received.
   */
  conversationIsActive: boolean
}

/**
 * Creates a stream of system notifications based on incoming messages.
 */
export function incomingJobMessageNotifications({
  incomingMessage$,
  pageActivity$,
  userActive$,
  activeJobConversationId$,
  onAction,
}: IncomingJobMessageNotificationsOptions): Observable<SystemNotification> {
  return incomingMessage$.pipe(
    withLatestFrom(
      userActive$,
      pageActivity$,
      activeJobConversationId$.pipe(startWith(null)),
    ),
    mergeMap(([incoming, userActive, pageActivity, activeConversationId]) => {
      const { message, conversation } = incoming
      if (conversation.meta.type !== 'Job') {
        return EMPTY
      }

      const conversationIsActive =
        message.conversationId === activeConversationId
      const shouldShowNotification =
        !userActive || !pageActivity.focused || !conversationIsActive

      if (shouldShowNotification) {
        return of({
          conversationIsActive,
          message,
          jobId: conversation.meta.id,
        })
      }

      return EMPTY
    }),
    map(
      ({ message, jobId, conversationIsActive }): SystemNotification => ({
        title: message.sender.displayName,
        body: message.plainText,
        tag: message.cid,
        icon: message.sender.avatarUrl,
        onClick: () =>
          onAction({
            jobId: Id.assert(jobId),
            conversationIsActive,
          }),
        // Automatically close when the user has read the message.
        close$: toStream(
          () => message.currentUserStatus?.deliveryStatus === 'Read',
          true,
        ).pipe(filter(Boolean)),
      }),
    ),
  )
}
